import React, { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Calendar as CalendarIcon, Loader2 } from "lucide-react";
import { formatDate, toISODate, usDateToISO } from "@/lib/dates";

/*
 * The calendar is its own chunk (see DateFieldCalendar), fetched by hand
 * rather than through React.lazy (T21-M7), for three things lazy cannot do:
 *
 *   * Retry. A chunk that 404s — a deploy replaced it mid-session — rejects,
 *     and the next attempt fetches again instead of reusing the failure.
 *   * Preload. Focusing the field, or pointing at the button, starts the
 *     fetch, so the tap that opens the calendar usually finds it loaded.
 *   * Say it is loading. The popover only mounts once the code is here; until
 *     then the button spins and nothing opens empty.
 *
 * A failure costs the calendar, never the page: the button goes away, focus
 * goes to the field, and the date is typed.
 */
const importCalendar = () => import("./DateFieldCalendar");
let calendarLoader = importCalendar;
let calendarModule = null;
let calendarPromise = null;

export function loadCalendar() {
  if (calendarModule) return Promise.resolve(calendarModule);
  if (!calendarPromise) {
    calendarPromise = calendarLoader()
      .then((m) => { calendarModule = m.default; return calendarModule; })
      .catch((err) => { calendarPromise = null; throw err; });
  }
  return calendarPromise;
}

const preloadCalendar = () => { loadCalendar().catch(() => {}); };

/** Tests only: swap the chunk loader (null restores the real one) and forget what was loaded. */
export function __setCalendarLoaderForTests(loader) {
  calendarLoader = loader || importCalendar;
  calendarModule = null;
  calendarPromise = null;
}

/**
 * The one date input (T21-M2): typed as MM/DD/YYYY or picked from a calendar,
 * emitted as `"YYYY-MM-DD"`.
 *
 * It replaces `<input type="date">`, whose visible format belongs to the
 * browser's language and not to the page: a Chrome set to Spanish shows
 * 05/10/2026 for October 5th whatever `lang` says. The value that crosses
 * `onChange` is the same wire string the native input produced, so no form,
 * payload or comparison around it changes.
 *
 * **`value` is only ever a real date or "".** While the text is incomplete or
 * unreadable the field emits "", so the form never holds a date the person no
 * longer sees, and the field says what is wrong once they leave it.
 *
 * **And "" is not "no date".** A form must not send "" for text it could not
 * read: `onValidityChange(valid)` tells it whether the field may be sent, and
 * `showError` makes the field show why once a send has been refused. Empty is
 * valid unless `required`.
 *
 * `InputComponent` lets a design-system screen pass the ds `Input`; anything
 * that accepts the props of an `<input>` works. A plain `<input>` gets the
 * app's `input-base` unless the caller names another class.
 */

/**
 * Keystrokes → MM/DD/YYYY. Digits fill month, day, year in turn and the
 * slashes arrive on their own; a typed slash closes a one-digit month or day
 * ("1/5/2026" is accepted and padded when the field is left). A pasted ISO
 * date is turned around.
 */
export function maskUSDate(raw) {
  const text = String(raw ?? "");
  const iso = /^\s*(\d{4})-(\d{2})-(\d{2})\s*$/.exec(text);
  if (iso) return `${iso[2]}/${iso[3]}/${iso[1]}`;
  const max = [2, 2, 4];
  const seg = [""];
  // A segment a full one overflowed into. The slash that follows it is the
  // old boundary, pushed along by a digit inserted earlier in the text: it is
  // absorbed rather than closing a one-digit segment ("10/0", "2" typed
  // after the "1" → "12/00", not "12/0/0").
  let carried = false;
  for (const ch of text) {
    const i = seg.length - 1;
    if (ch >= "0" && ch <= "9") {
      if (seg[i].length < max[i]) seg[i] += ch;
      else if (i < 2) { seg.push(ch); carried = true; }
      else break;
    } else if (ch === "/" && carried) {
      carried = false;
    } else if (ch === "/" && i < 2 && seg[i].length > 0) {
      seg.push("");
    }
  }
  return seg.join("/");
}

/**
 * One keystroke, as the field should take it (T21-M6). `prev` is what the
 * field showed, `raw` what the browser made of it, `caret` where the browser
 * left the cursor. Returns the masked text and where the cursor goes in it.
 *
 * Deleting a slash on its own would do nothing — the mask puts it straight
 * back — so a key that removes only a slash removes the digit beside it
 * instead (the one before for Backspace, after for Delete) and keeps the
 * slash: "10/05/2026", Backspace on the first slash → "1/05/2026". The
 * cursor keeps the same number of digits to its left, so the slashes the
 * mask adds never throw it to the end of the field.
 */
export function editUSDate(prev, raw, caret, inputType) {
  let text = String(raw ?? "");
  let at = caret ?? text.length;
  if (text.length === prev.length - 1 && maskUSDate(text) === prev) {
    let i = 0;
    while (i < text.length && text[i] === prev[i]) i += 1;
    if (prev[i] === "/") {
      if (inputType === "deleteContentForward") {
        text = prev.slice(0, i + 1) + prev.slice(i + 2);
        at = i + 1;
      } else if (i > 0) {
        text = prev.slice(0, i - 1) + prev.slice(i);
        at = i - 1;
      }
    }
  }
  const masked = maskUSDate(text);
  const digits = text.slice(0, at).replace(/\D/g, "").length;
  let pos = 0;
  for (let seen = 0; pos < masked.length && seen < digits; pos += 1) {
    if (masked[pos] >= "0" && masked[pos] <= "9") seen += 1;
  }
  return { text: masked, caret: pos };
}

const inRange = (iso, min, max) => !!iso && (!min || iso >= min) && (!max || iso <= max);

export default function DateField({
  value, onChange, onValidityChange, showError = false,
  min, max, id, disabled = false, required = false,
  className, style, InputComponent = "input",
  "aria-label": ariaLabel, "data-testid": testId,
}) {
  const { t } = useTranslation();
  const ownId = useId();
  const inputId = id || `date-${ownId}`;
  const errorId = `${inputId}-error`;
  const calendarId = `${inputId}-calendar`;
  const current = value || "";
  const anchorRef = useRef(null);
  const buttonRef = useRef(null);

  const [text, setText] = useState(() => formatDate(current));
  const [left, setLeft] = useState(false);
  const [open, setOpen] = useState(false);
  const [Calendar, setCalendar] = useState(() => calendarModule);
  // "idle" | "loading" | "failed"
  const [calendarState, setCalendarState] = useState("idle");
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);
  // Still wanted when the code arrives: false if they pressed again meanwhile.
  const wanted = useRef(false);

  const toggleCalendar = () => {
    if (open || calendarState === "loading") {
      wanted.current = false;
      setOpen(false);
      setCalendarState("idle");
      return;
    }
    if (calendarModule) {
      setCalendar(() => calendarModule);
      setOpen(true);
      return;
    }
    wanted.current = true;
    setCalendarState("loading");
    loadCalendar().then((mod) => {
      if (!mounted.current) return;
      setCalendar(() => mod);
      setCalendarState("idle");
      if (wanted.current) setOpen(true);
      wanted.current = false;
    }).catch((err) => {
      console.warn("[DateField] calendar chunk failed to load", err);
      if (!mounted.current) return;
      wanted.current = false;
      setCalendarState("failed");
      anchorRef.current?.querySelector("input")?.focus();
    });
  };

  // A value set from outside — a draft loaded, a form reset — replaces the
  // text. One the field emitted itself already matches it and is left alone,
  // which is what lets a half-typed date survive its own "".
  useEffect(() => {
    const typed = usDateToISO(text);
    const shown = inRange(typed, min, max) ? typed : "";
    if (current !== shown) setText(formatDate(current));
    // `text` is deliberately not a dependency: this answers the parent only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, min, max]);

  const emit = (iso) => {
    if (iso !== current) onChange?.(iso);
  };

  // Typing, pasting and autofill all arrive here: React's onChange is fed by
  // both `input` and `change`, and a paste or an autofill is one event with
  // the whole value in it.
  const caretRef = useRef(null);
  const apply = (prev, el, inputType) => {
    const next = editUSDate(prev, el.value, el.selectionStart, inputType);
    caretRef.current = next.caret;
    setText(next.text);
    const iso = usDateToISO(next.text);
    emit(inRange(iso, min, max) ? iso : "");
  };

  // An IME composition (GBoard, Samsung's keyboard) owns the text until it
  // ends. Re-masking in the middle of one rewrites the value under the IME,
  // which then commits into a range that no longer exists: "10052026" came
  // out "10/00/5520" in Chrome (T21-M13). So while composing the field only
  // mirrors what the IME wrote, and the mask runs once, on compositionend,
  // against the text from before the composition began.
  const composing = useRef(null); // the text when the composition started
  const onType = (e) => {
    if (composing.current !== null || e.nativeEvent?.isComposing) {
      setText(e.target.value);
      return;
    }
    apply(text, e.target, e.nativeEvent?.inputType);
  };
  const onCompositionStart = () => {
    if (composing.current === null) composing.current = text;
  };
  const onCompositionEnd = (e) => {
    const before = composing.current ?? text;
    composing.current = null;
    apply(before, e.target, "insertCompositionText");
  };
  useLayoutEffect(() => {
    const pos = caretRef.current;
    caretRef.current = null;
    const el = anchorRef.current?.querySelector("input");
    if (pos !== null && el && el === document.activeElement) el.setSelectionRange(pos, pos);
  }, [text]);

  const onLeave = () => {
    setLeft(true);
    const iso = usDateToISO(text);
    if (iso) setText(formatDate(iso)); // "1/5/2026" → "01/05/2026"
  };

  const pick = (day) => {
    const iso = toISODate(day);
    setText(formatDate(iso));
    setLeft(false);
    emit(iso);
    setOpen(false);
  };

  // What is wrong with the field right now, shown or not. `valid` is what the
  // form asks before it sends: "" emitted for half-typed text must never be
  // read as "no date" (T21-M5).
  const typedISO = usDateToISO(text);
  let problem = null;
  if (text) {
    if (!typedISO) problem = "invalid";
    else if (!inRange(typedISO, min, max)) problem = "outOfRange";
  } else if (required) {
    problem = "required";
  }
  const valid = !problem;

  const reportRef = useRef(onValidityChange);
  reportRef.current = onValidityChange;
  useEffect(() => { reportRef.current?.(valid); }, [valid]);
  // A field taken off the screen (the dates switched back to "as soon as
  // possible", Dany's card closing its editor) holds nothing any more, and
  // what is sent is what is shown: no date.
  useEffect(() => () => reportRef.current?.(true), []);

  // Shown once they leave the field, as soon as a typed date is complete, or
  // when the form says a send was refused because of it.
  const visible = problem && (showError || left || (problem !== "required" && text.length >= 10));
  const error = !visible ? null
    : problem === "required" ? t("common.required")
      : t(`dateField.${problem}`);
  const Field = InputComponent;

  return (
    <>
      <div ref={anchorRef} style={{ position: "relative", width: "100%" }}>
        <Field
          id={inputId}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          maxLength={10}
          placeholder={t("dateField.placeholder")}
          value={text}
          onChange={onType}
          onCompositionStart={onCompositionStart}
          onCompositionEnd={onCompositionEnd}
          onFocus={preloadCalendar}
          onBlur={onLeave}
          disabled={disabled}
          required={required}
          aria-label={ariaLabel}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          className={className ?? (InputComponent === "input" ? "input-base" : undefined)}
          // 16px at least, after the caller's style: below it iOS zooms the
          // page in on focus (T21-M6).
          style={{ paddingRight: 48, ...style, fontSize: "16px" }}
          invalid={InputComponent === "input" ? undefined : !!error}
          data-testid={testId}
        />
        {calendarState !== "failed" && <button
          ref={buttonRef}
          type="button"
          disabled={disabled}
          aria-label={t("dateField.openCalendar")}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-controls={open ? calendarId : undefined}
          aria-busy={calendarState === "loading" || undefined}
          onClick={toggleCalendar}
          onPointerEnter={preloadCalendar}
          onFocus={preloadCalendar}
          data-testid={testId ? `${testId}-calendar` : undefined}
          style={{
            position: "absolute", right: 2, top: "50%", transform: "translateY(-50%)",
            width: "var(--touch-min)", height: "var(--touch-min)",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            background: "transparent", border: 0, borderRadius: "var(--radius-sm)",
            color: "var(--text-muted)", cursor: disabled ? "default" : "pointer",
          }}
        >
          {calendarState === "loading"
            ? <Loader2 size={18} className="animate-spin" aria-hidden="true" />
            : <CalendarIcon size={18} aria-hidden="true" />}
        </button>}
      </div>
      {error && (
        <p id={errorId} role="alert" data-testid={testId ? `${testId}-error` : undefined}
           style={{ marginTop: 4, fontSize: "var(--text-xs)", color: "var(--danger)" }}>
          {error}
        </p>
      )}
      {open && Calendar && (
        <Calendar
          id={calendarId} anchorRef={anchorRef} triggerRef={buttonRef}
          value={current} min={min} max={max}
          onPick={pick} onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}
